// HeaderMainPage—ModernDark.jsx
// Drop-in replacement for your existing header
// - Rich, modern dark UI with glass/gradient, smooth interactions, and a tighter layout
// - Routes, API calls, and behaviors remain the same as your current implementation

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import whiteLogo from "../assets/Logo.png";
import {
  Gauge,
  Megaphone,
  ReceiptText,
  PlusCircle,
  List,
  ChevronDown,
  Coins,
  Menu,
  X,
  User,
  LogOut,
} from "lucide-react";

/**
 * Dark PRO Header (post-login)
 * Visual refresh only — keeps existing flows, routes, and credits fetch logic.
 */

export const newCampaign = (navigate) => {
  const keysToKeep = [
    "access_token",
    "isLoggedIn",
    "havedata",
    "nickname",
    "linkedinUrl",
    "email",
    "organizationName",
    "profile_picture",
    "userType",
    "lnk_act",
    "page3_audio_data",
  ];
  Object.keys(localStorage).forEach((key) => {
    if (!keysToKeep.includes(key)) localStorage.removeItem(key);
  });
  localStorage.removeItem("conversation_id");

  const haveData = JSON.parse(localStorage.getItem("havedata"));
  const haveLinkedIN = JSON.parse(localStorage.getItem("lnk_act"));
  if (haveData || haveLinkedIN) navigate("/campinitselect");
  else navigate("/onboarding-userinfo");
};

const classNames = (...s) => s.filter(Boolean).join(" ");

const normalizeUserProfile = (source = {}) => ({
  name: source.name ?? source.nickname ?? source.nick_name ?? source.user_info?.nick_name ?? "",
  email: source.email ?? "",
  organizationName:
    source.organizationName ??
    source.organization_name ??
    source.user_info?.organization_name ??
    "",
  profilePicture:
    source.profilePicture ??
    source.profile_picture ??
    "",
});

const readStoredUserProfile = () =>
  normalizeUserProfile({
    nickname: localStorage.getItem("nickname") || "",
    email: localStorage.getItem("email") || "",
    organizationName: localStorage.getItem("organizationName") || "",
    profilePicture: localStorage.getItem("profile_picture") || "",
  });

const persistUserProfile = (profile) => {
  localStorage.setItem("nickname", profile.name || "");
  localStorage.setItem("email", profile.email || "");
  localStorage.setItem("organizationName", profile.organizationName || "");
  localStorage.setItem("profile_picture", profile.profilePicture || "");
};

const HeaderAvatar = ({ profile, className = "w-9 h-9" }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const fallbackSource = (profile.name || profile.email || "R").trim();
  const fallbackInitial = fallbackSource ? fallbackSource.charAt(0).toUpperCase() : "R";

  useEffect(() => {
    setImageFailed(false);
  }, [profile.profilePicture]);

  if (profile.profilePicture && !imageFailed) {
    return (
      <img
        src={profile.profilePicture}
        alt={profile.name || profile.email || "Profile"}
        className={`${className} rounded-full object-cover ring-1 ring-white/20`}
        onError={() => setImageFailed(true)}
      />
    );
  }

  return (
    <div
      className={`${className} rounded-full bg-gradient-to-br from-sky-500 via-indigo-500 to-violet-500 text-white ring-1 ring-white/20 flex items-center justify-center font-semibold`}
      aria-hidden="true"
    >
      {fallbackInitial}
    </div>
  );
};

const HeaderMainPage = () => {
  const navigate = useNavigate();

  // State
  const [showCampaignMenu, setShowCampaignMenu] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [userCredit, setUserCredit] = useState(null);
  const [showCreditDetails, setShowCreditDetails] = useState(false);
  const [userProfile, setUserProfile] = useState(readStoredUserProfile);

  const campaignMenuRef = useRef(null);
  const profileMenuRef = useRef(null);
  const profileButtonRef = useRef(null);
  const navRef = useRef(null);

  const roboman_api = process.env.REACT_APP_ROBOMAN_API;

  // Buttons (dark glass)
  const navBtn = classNames(
    "relative inline-flex items-center justify-center h-10 px-4 rounded-xl text-sm font-medium",
    "text-slate-200 hover:text-white",
    "bg-white/0 hover:bg-white/10 active:bg-white/15",
    "ring-1 ring-white/10",
    "transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
  );

  // Close on outside click + ESC
  useEffect(() => {
    const onDown = (e) => {
      if (e.key === "Escape") {
        setShowCampaignMenu(false);
        setShowProfileMenu(false);
        setIsNavOpen(false);
      }
    };
    const onClick = (e) => {
      if (campaignMenuRef.current && !campaignMenuRef.current.contains(e.target)) {
        setShowCampaignMenu(false);
      }


      // Profile menu - button aur menu dono check karo
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(e.target) &&
        profileButtonRef.current &&
        !profileButtonRef.current.contains(e.target)
      ) {
        setShowProfileMenu(false);
      }
      if (
        isNavOpen &&
        navRef.current &&
        !navRef.current.contains(e.target) &&
        !e.target.closest("[data-hamburger]")
      ) {
        setIsNavOpen(false);
      }

      if (navRef.current && !navRef.current.contains(e.target)) {
        setIsNavOpen(false);
      }
    };
    document.addEventListener("keydown", onDown);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onDown);
      document.removeEventListener("mousedown", onClick);
    };
  }, [isNavOpen]);

  // Actions
  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };
  const Dashboard = () => navigate("/dashboard");
  const myCampaign = () => {
    localStorage.removeItem("campaign_id");
    navigate("/my-campaign");
  };
  const BillingPage = () => {
    navigate("/pricing");
    localStorage.setItem("showbillpage", true);
  };

  const fetchUserProfile = async () => {
    try {
      const accessToken = localStorage.getItem("access_token");
      if (!accessToken || !roboman_api) return;

      const response = await fetch(`${roboman_api}/users/me`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: accessToken,
        },
      });

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      const nextProfile = normalizeUserProfile(data);
      persistUserProfile(nextProfile);
      setUserProfile(nextProfile);
    } catch (err) {
      console.error("Failed to fetch profile summary:", err);
    }
  };

  // Credits fetch
  const fetchUserCredits = async () => {
    try {
      const response = await fetch(`${roboman_api}/users/credits`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: localStorage.getItem("access_token"),
        },
      });
      const data = await response.json();
      if (response.ok) setUserCredit(data);
      else console.error("Failed to fetch credit");
    } catch (err) {
      console.error("Network or parsing error:", err);
    }
  };

  useEffect(() => {
    fetchUserCredits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchUserProfile();

    const handleUserProfileUpdated = (event) => {
      const nextProfile = normalizeUserProfile(event.detail || {});
      persistUserProfile(nextProfile);
      setUserProfile(nextProfile);
    };

    window.addEventListener("roboman:user-profile-updated", handleUserProfileUpdated);
    return () => {
      window.removeEventListener("roboman:user-profile-updated", handleUserProfileUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roboman_api]);

  // Derived credit metrics
  const current = Number(userCredit?.current_credits) || 0;
  const used = Number(userCredit?.used_credits) || 0;
  const bought = Number(userCredit?.bought_credits) || 0;
  const total = Math.max(current + used, bought || 0) || current + used;
  const utilization = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const remainingPct = Math.max(0, 100 - utilization);

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <header className="w-full sticky top-0 z-40" aria-label="Primary">
      {/* Top gradient rail */}
      <div className="h-[2px] w-full bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600" />

      {/* Dark glass bar */}
      <div className="backdrop-blur-xl bg-[#0b1220]/90 border-b border-white/10 shadow-[0_8px_30px_-12px_rgba(0,0,0,.45)]">
        <div className="max-w-7xl mx-auto">
          <div className="min-h-[56px] sm:min-h-[64px] px-3 sm:px-6 lg:px-8 flex items-center">
            {/* Brand */}
            <button
              onClick={Dashboard}
              className="flex items-center gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded-md"
              aria-label="Go to dashboard"
            >
              <img
                src={whiteLogo}
                alt="Outreach"
                className="h-15 sm:h-7 md:h-8 lg:h-9 w-auto max-w-[120px] xs:max-w-[140px] sm:max-w-[180px] object-contain"
              />
              <span className="sr-only">Outreach</span>
            </button>

            {/* Right block */}
            <div className="ml-auto flex items-center gap-3" ref={profileMenuRef}>
              {/* Desktop nav */}
              <nav className="hidden lg:flex items-center gap-2 mr-2">
                <button onClick={Dashboard} className={navBtn}>
                  <Gauge className="mr-2 h-4 w-4" />
                  Dashboard
                </button>

                <div className="relative" ref={campaignMenuRef}>
                  <button
                    onClick={() => {
                      setShowCampaignMenu((s) => !s);
                      setShowProfileMenu(false);
                    }}
                    className={classNames(navBtn, "pr-10")}
                    aria-expanded={showCampaignMenu}
                    aria-haspopup="menu"
                  >
                    <Megaphone className="mr-2 h-4 w-4" />
                    Campaign
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-80" />
                  </button>

                  {showCampaignMenu && (
                    <div
                      role="menu"
                      className="absolute top-[110%] right-0 bg-[#0f1729] text-slate-100 rounded-xl shadow-2xl ring-1 ring-white/10 z-40 min-w-[220px] overflow-hidden animate-in fade-in-0 zoom-in-95"
                    >
                      <button
                        onClick={() => newCampaign(navigate)}
                        className="flex items-center w-full h-[48px] text-left px-4 hover:bg-white/5 font-medium gap-2"
                        role="menuitem"
                      >
                        <PlusCircle className="text-indigo-400 h-4 w-4" />
                        New Campaign
                      </button>
                      <button
                        onClick={myCampaign}
                        className="flex items-center w-full h-[48px] text-left px-4 hover:bg-white/5 font-medium gap-2"
                        role="menuitem"
                      >
                        <List className="text-indigo-400 h-4 w-4" />
                        List of Campaign
                      </button>
                    </div>
                  )}
                </div>

                <button onClick={BillingPage} className={navBtn}>
                  <ReceiptText className="mr-2 h-4 w-4" />
                  Billing
                </button>
              </nav>

              {/* Credits pill */}
              <div
                className="group relative hidden lg:flex items-center gap-3 text-sm font-semibold py-1.5 pl-3 pr-3 rounded-full cursor-default transition-all bg-white/10 ring-1 ring-white/10 hover:bg-white/15 text-slate-100"
                onMouseEnter={() => setShowCreditDetails(true)}
                onMouseLeave={() => setShowCreditDetails(false)}
                aria-label="Credits"
                title="Credits"
              >
                <span className="tabular-nums">{current}</span>

                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-amber-300/20 ring-1 ring-amber-300/30">

                  <Coins className="h-4 w-4 text-amber-300" />
                </span>

                {/* micro bar */}
                <div className="hidden md:flex items-center w-24 h-2.5 rounded-full bg-white/10 overflow-hidden ring-1 ring-white/10">
                  <div className="h-full bg-amber-400 transition-all" style={{ width: `${utilization}%` }} />
                  <div className="h-full bg-emerald-400/80 transition-all" style={{ width: `${remainingPct}%` }} />
                </div>

                <span className="hidden md:inline text-xs text-slate-300 tabular-nums">{utilization}%</span>

                {/* Popover */}
                {showCreditDetails && (
                  <div className="absolute top-[120%] right-0 bg-[#0f1729]/95 backdrop-blur-xl text-slate-100 rounded-xl shadow-2xl ring-1 ring-white/10 p-4 w-80 z-40 animate-in fade-in-0 slide-in-from-top-2">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold">Credit Utilization</div>
                      <div className="text-xs text-slate-400">Updated: {formatDate(userCredit?.updated_at)}</div>
                    </div>

                    <div className="mt-1">
                      <div className="flex items-center justify-between text-xs text-slate-300 mb-1">
                        <span>Used</span>
                        <span className="tabular-nums">{used}</span>
                      </div>
                      <div className="w-full h-3 rounded-full bg-white/10 overflow-hidden ring-1 ring-white/10">
                        <div className="h-full bg-gradient-to-r from-amber-400 to-amber-500" style={{ width: `${utilization}%` }} />
                      </div>
                      <div className="mt-2 flex items-center justify-between text-xs text-slate-300">
                        <span>Remaining</span>
                        <span className="tabular-nums">{current}</span>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
                      <div className="rounded-lg bg-white/5 ring-1 ring-white/10 p-3">
                        <div className="text-xs text-slate-400">Total</div>
                        <div className="font-semibold tabular-nums">{total}</div>
                      </div>
                      <div className="rounded-lg bg-white/5 ring-1 ring-white/10 p-3">
                        <div className="text-xs text-slate-400">Bought</div>
                        <div className="font-semibold tabular-nums">{bought}</div>
                      </div>
                      <div className="rounded-lg bg-white/5 ring-1 ring-white/10 p-3">
                        <div className="text-xs text-slate-400">Utilization</div>
                        <div className="font-semibold tabular-nums">{utilization}%</div>
                      </div>
                    </div>

                    <div className="mt-4">
                      <button
                        onClick={() => navigate("/pricing")}
                        className="w-full inline-flex items-center justify-center rounded-lg text-sm font-semibold text-white px-4 py-2 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                      >
                        Manage / Buy Credits
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Profile menu */}
              <div className="relative">
                <button
                  onClick={() => {
                    setShowProfileMenu((s) => !s);
                    setShowCampaignMenu(false);
                  }}
                  className="flex items-center gap-2 rounded-xl hover:bg-white/10 transition-all px-2 py-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 text-slate-100"
                  aria-haspopup="menu"
                  aria-expanded={showProfileMenu}
                >
                  <div className="hidden xl:flex flex-col items-end leading-tight">
                    <span className="max-w-[160px] truncate text-sm font-semibold text-slate-100">
                      {userProfile.name || userProfile.email || "User Account"}
                    </span>
                    <span className="max-w-[160px] truncate text-xs text-slate-400">
                      {userProfile.organizationName || userProfile.email || "Profile"}
                    </span>
                  </div>
                  <HeaderAvatar profile={userProfile} />
                  <ChevronDown className="h-4 w-4 opacity-80" />
                </button>

                {showProfileMenu && (
                  <div
                    ref={profileButtonRef}
                    role="menu"
                    className="absolute top-[110%] right-0 bg-[#0f1729] text-slate-100 rounded-xl shadow-2xl ring-1 ring-white/10 z-40 overflow-hidden min-w-[280px] animate-in fade-in-0 zoom-in-95"
                  >
                    <div className="flex items-center gap-3 border-b border-white/10 px-4 py-4">
                      <HeaderAvatar profile={userProfile} className="w-11 h-11" />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-white">
                          {userProfile.name || "User Account"}
                        </div>
                        <div className="truncate text-xs text-slate-400">
                          {userProfile.email || "No email available"}
                        </div>
                        {userProfile.organizationName ? (
                          <div className="truncate text-xs text-sky-300">
                            {userProfile.organizationName}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <button
                      className="flex items-center gap-2 w-full h-[48px] px-4 text-left hover:bg-white/5 font-medium"
                      onClick={() => {
                        navigate("/account");
                        setShowProfileMenu(false);
                      }}
                      role="menuitem"
                    >
                      <User className="h-4 w-4 text-indigo-400" />
                      User Account
                    </button>
                    <button
                      className="flex items-center gap-2 w-full h-[48px] px-4 text-left hover:bg-white/5 font-medium text-red-300"
                      onClick={handleLogout}
                      role="menuitem"
                    >
                      <LogOut className="h-4 w-4" />
                      Logout
                    </button>
                  </div>
                )}
              </div>

              {/* Mobile hamburger */}
              <button
                data-hamburger
                className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-xl hover:bg-white/10 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 text-slate-100"
                onClick={() => setIsNavOpen((s) => !s)}
                aria-label="Open menu"
              >
                {isNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sheet + Overlay */}
      {isNavOpen && (
        <div className="lg:hidden">
          {/* Overlay */}
          <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]" />

          {/* Sheet */}
          <div
            ref={navRef}
            className="fixed top-0 left-0 right-0 z-50 max-h-[85dvh] overflow-y-auto
                 overscroll-contain bg-[#0f1729]/95 backdrop-blur-xl
                 text-slate-100 rounded-b-2xl shadow-2xl ring-1 ring-white/10"
          >
            <div className="px-4 pt-4 pb-5">

              {/* Header */}
              <div className="flex items-center justify-between">
                <img src={whiteLogo} alt="Outreach" className="h-7 object-contain" />
                <button
                  onClick={() => setIsNavOpen(false)}
                  className="h-9 w-9 inline-flex items-center justify-center rounded-lg
                       hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-indigo-500"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-4 flex items-center gap-3 rounded-xl bg-white/5 p-3 ring-1 ring-white/10">
                <HeaderAvatar profile={userProfile} className="h-11 w-11" />
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-white">
                    {userProfile.name || userProfile.email || "User Account"}
                  </div>
                  <div className="truncate text-xs text-slate-400">
                    {userProfile.email || "No email available"}
                  </div>
                  {userProfile.organizationName ? (
                    <div className="truncate text-xs text-sky-300">
                      {userProfile.organizationName}
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Main Nav */}
              <div className="mt-4 flex flex-col gap-2">

                {/* Dashboard */}
                <button
                  onClick={() => {
                    Dashboard();
                    setIsNavOpen(false);
                  }}
                  className="h-10 rounded-lg bg-white/5 ring-1 ring-white/10
                       hover:bg-white/10 px-3 text-sm font-medium
                       flex items-center gap-2"
                >
                  <Gauge className="h-4 w-4 opacity-80" />
                  Dashboard
                </button>

                {/* Campaign */}
                <div className="relative">
                  <button
                    onClick={() => setShowCampaignMenu(s => !s)}
                    className="h-10 w-full rounded-lg bg-white/5 ring-1 ring-white/10
                         hover:bg-white/10 px-3 text-sm font-medium
                         flex items-center justify-between"
                  >
                    <span className="flex items-center gap-2">
                      <Megaphone className="h-4 w-4 opacity-80" />
                      Campaign
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-70" />
                  </button>

                  {showCampaignMenu && (
                    <div className="mt-1 rounded-lg bg-[#0f1729]
                              ring-1 ring-white/10 overflow-hidden"
                      ref={navRef}
                      onMouseDown={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => {
                          newCampaign(navigate);
                          setIsNavOpen(false);
                        }}
                        className="h-9 w-full px-3 text-sm flex items-center gap-2
                             hover:bg-white/5"
                      >
                        <PlusCircle className="h-4 w-4 text-indigo-400" />
                        New Campaign
                      </button>

                      <button
                        onClick={() => {
                          myCampaign();
                          setIsNavOpen(false);
                        }}
                        className="h-9 w-full px-3 text-sm flex items-center gap-2
                             hover:bg-white/5"
                      >
                        <List className="h-4 w-4 text-indigo-400" />
                        Campaign List
                      </button>
                    </div>
                  )}
                </div>

                {/* Billing */}
                <button
                  onClick={() => {
                    BillingPage();
                    setIsNavOpen(false);
                  }}
                  className="h-10 rounded-lg bg-white/5 ring-1 ring-white/10
                       hover:bg-white/10 px-3 text-sm font-medium
                       flex items-center gap-2"
                >
                  <ReceiptText className="h-4 w-4 opacity-80" />
                  Billing
                </button>
              </div>

              {/* Account + Logout */}
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  onClick={() => {
                    navigate("/account");
                    setIsNavOpen(false);
                  }}
                  className="h-9 rounded-lg bg-white/5 ring-1 ring-white/10
                       hover:bg-white/10 text-sm flex items-center
                       justify-center gap-2"
                >
                  <User className="h-4 w-4" />
                  Account
                </button>

                <button
                  onClick={handleLogout}
                  className="h-9 rounded-lg bg-red-500/90 hover:bg-red-600
                       text-sm flex items-center justify-center gap-2"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </div>

              {/* Credits (Compact) */}
              <div className="mt-3 rounded-xl bg-white/5 ring-1 ring-white/10 p-3">
                <div className="flex items-center justify-between text-xs mb-1">
                  <div className="flex items-center gap-2 font-medium">
                    <Coins className="h-4 w-4 text-amber-400" />
                    Credits
                  </div>
                  <span className="text-slate-400">
                    updated: {formatDate(userCredit?.updated_at)}
                  </span>
                </div>

                {/* Progress */}
                <div className="mt-1">
                  <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-amber-400 to-amber-500"
                      style={{ width: `${utilization}%` }}
                    />
                  </div>
                </div>

                {/* Inline stats */}
                <div className="mt-2 flex justify-between text-xs text-slate-300">
                  <span>Total: <b>{total}</b></span>
                  <span>Used: <b>{used}</b></span>
                  <span>Utilization: <b>{utilization}%</b></span>
                </div>

                {/* CTA */}
                <button
                  onClick={() => {
                    navigate("/pricing");
                    setIsNavOpen(false);
                  }}
                  className="mt-3 h-9 w-full rounded-lg text-sm font-medium
                       bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 hover:from-indigo-500 hover:via-violet-500 hover:to-fuchsia-500"
                >
                  Buy Credits
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

    </header>
  );
};

export default HeaderMainPage;
